import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { apiClient } from '@/lib/axios';
import { Button } from '@/components/ui';

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError, loginAttemptsRemaining, rateLimitRetryAfterSeconds } =
    useAuthStore();

  const [view, setView] = useState<'login' | 'forgot-password'>('login');

  // Ticks down locally once the backend reports a lockout, so the message and the
  // disabled Sign in button update every second without polling the server.
  const [lockoutSeconds, setLockoutSeconds] = useState<number | null>(null);

  useEffect(() => {
    if (rateLimitRetryAfterSeconds != null) {
      setLockoutSeconds(rateLimitRetryAfterSeconds);
    }
  }, [rateLimitRetryAfterSeconds]);

  useEffect(() => {
    if (lockoutSeconds === null || lockoutSeconds <= 0) return;
    const timer = setTimeout(() => setLockoutSeconds((s) => (s === null ? null : s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [lockoutSeconds]);

  const isLockedOut = lockoutSeconds !== null && lockoutSeconds > 0;

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [resetIdentifier, setResetIdentifier] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState<string | null>(null);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isLockedOut) return;
    clearError();
    try {
      await login(identifier, password);
      navigate('/dashboard', { replace: true });
    } catch {
      // Error is already set in the store
    }
  };

  const goToForgotPassword = () => {
    clearError();
    setChangePasswordError(null);
    setChangePasswordSuccess(null);
    setResetIdentifier(identifier);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setView('forgot-password');
  };

  const goToSignIn = () => {
    setChangePasswordError(null);
    setChangePasswordSuccess(null);
    setView('login');
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setChangePasswordError(null);
    setChangePasswordSuccess(null);

    if (newPassword !== confirmPassword) {
      setChangePasswordError('New password and confirm password do not match.');
      return;
    }

    setIsChangingPassword(true);
    try {
      await apiClient.post('/auth/change-password', {
        identifier: resetIdentifier,
        currentPassword,
        newPassword,
        confirmPassword,
      });
      setChangePasswordSuccess('Password updated successfully. You can now sign in.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { message?: string } } }).response?.data?.message ??
            'Failed to update password.')
          : 'Failed to update password.';
      setChangePasswordError(message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 flex items-center justify-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent/15">
            <span className="text-lg font-bold text-accent">D</span>
          </div>
          <div>
            <h1 className="text-xl font-semibold text-text-primary">Deployment Server</h1>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-border-subtle bg-surface-0 p-6 shadow-[var(--shadow-floating)]">
          {view === 'login' ? (
            <>
              <h2 className="mb-5 text-sm font-semibold text-text-primary">Sign in to your account</h2>

              {isLockedOut ? (
                <div
                  role="alert"
                  className="mb-4 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger"
                >
                  Too many login attempts. Please wait {lockoutSeconds}s before trying again.
                </div>
              ) : (
                error && (
                  <div
                    role="alert"
                    className="mb-4 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger"
                  >
                    {error}
                  </div>
                )
              )}

              {!isLockedOut && loginAttemptsRemaining !== null && loginAttemptsRemaining > 0 && (
                <p className="mb-4 -mt-2 text-xs text-text-tertiary">
                  {loginAttemptsRemaining} login attempt{loginAttemptsRemaining === 1 ? '' : 's'} left.
                </p>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="identifier" className="mb-1.5 block text-xs font-medium text-text-secondary">
                    Email or username
                  </label>
                  <input
                    id="identifier"
                    type="text"
                    autoComplete="username"
                    required
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
                    placeholder="jdoe or jdoe@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-text-secondary">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 pr-10 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="text-right">
                  <button
                    type="button"
                    onClick={goToForgotPassword}
                    className="text-xs font-medium text-accent hover:underline"
                  >
                    Forgot Password ?
                  </button>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  fullWidth
                  loading={isLoading}
                  disabled={isLockedOut}
                >
                  {isLockedOut ? `Wait ${lockoutSeconds}s` : 'Sign in'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="mb-5 text-sm font-semibold text-text-primary">Reset your password</h2>

              {changePasswordError && (
                <div
                  role="alert"
                  className="mb-4 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger"
                >
                  {changePasswordError}
                </div>
              )}

              {changePasswordSuccess && (
                <div
                  role="status"
                  className="mb-4 rounded-md border border-state-success/40 bg-state-success/10 px-3 py-2 text-xs text-state-success"
                >
                  {changePasswordSuccess}
                </div>
              )}

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label htmlFor="reset-identifier" className="mb-1.5 block text-xs font-medium text-text-secondary">
                    Email or username
                  </label>
                  <input
                    id="reset-identifier"
                    type="text"
                    autoComplete="username"
                    required
                    value={resetIdentifier}
                    onChange={(e) => setResetIdentifier(e.target.value)}
                    className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
                    placeholder="jdoe or jdoe@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="current-password" className="mb-1.5 block text-xs font-medium text-text-secondary">
                    Current password
                  </label>
                  <div className="relative">
                    <input
                      id="current-password"
                      type={showCurrentPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 pr-10 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                      aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="new-password" className="mb-1.5 block text-xs font-medium text-text-secondary">
                    New password
                  </label>
                  <div className="relative">
                    <input
                      id="new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 pr-10 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="mb-1.5 block text-xs font-medium text-text-secondary">
                    Confirm password
                  </label>
                  <div className="relative">
                    <input
                      id="confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      minLength={8}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 pr-10 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  fullWidth
                  loading={isChangingPassword}
                >
                  Update password
                </Button>

                <button
                  type="button"
                  onClick={goToSignIn}
                  className="block w-full text-center text-xs font-medium text-text-secondary hover:text-text-primary hover:underline"
                >
                  Back to Sign in
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
